import * as assets from '../../assets';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Input,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ImageButton from '../../components/sharedComponents/ImageButton';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'reduxStore/redux-hooks';
import { findEmptyFileName, showSnackbarErrorMessage } from 'components/staticComponents/staticUtiles';
import { enqueueSnackbar } from 'notistack';
import { setImageUploadData, updateFileSelectionState } from 'reduxStore/slices/FileUploadSlice';
import {
  supportedImageTypesForGeminiModels,
  supportedImageTypesForGPTModels,
} from 'components/rightPanel/subpanel/dataset/createDataset/DatasetModal';
import Api, { ApiError, SkillParameters } from '../../data/api/Api';
import {
  imageUploadNoteUrl,
  llmModelEnum,
  imageUploadNoteBrowser,
} from 'components/staticComponents/StaticHtmlGenerator';
import { setInitialHistoryState } from 'reduxStore/slices/historySlice';
import { LlmModel } from '../../data/models/LlmModel';
import ModelSelect from './ModelSelect';

interface imageUploadProps {
  disabled: boolean;
}

export enum ImageUploadOption {
  imageUrl = 'url',
  browseImage = 'browse',
  none = 'none',
}

const ImageUpload = ({ disabled }: imageUploadProps) => {
  const [showImageUploadOptions, setShowImageUploadOptions] = useState(false);
  const theme = useTheme();
  const [openImageDialog, setOpenImageDialog] = useState(true);
  const closeImageDialog = () => setOpenImageDialog(false);
  const [imageUploadOption, setImageUploadOption] = useState<
    ImageUploadOption.imageUrl | ImageUploadOption.browseImage
  >(ImageUploadOption.imageUrl);
  const [imageUrl, setImageUrl] = useState('');
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [allLlmModels, setAllLlmModels] = useState<LlmModel[]>([]); // Store all models fetched from the API
  const [llmModels, setLlmModels] = useState([LlmModel.defaultModal]);
  const [selectedModel, setSelectedModel] = useState(LlmModel.defaultModal);
  const [modifiedUrls, setModifiedUrls] = useState<string[]>([]);
  const [uploadedImageNames, setUploadedImageNames] = useState<string[]>([]);
  const [okButtonEnabled, setOkButtonEnabled] = useState(false);

  const handleImageUploadOptionsClick = () => {
    setOpenImageDialog(true);
    setShowImageUploadOptions(true);
    setOkButtonEnabled(false);
  };

  useEffect(() => {
    if (openImageDialog) {
      setImageUploadOption(ImageUploadOption.imageUrl);
    }
    setImageUrl('');
    setModifiedUrls([]);
    setUploadedImageNames([]);
    Api.listLlmModels().then((response) => {
      setAllLlmModels(response);
      filterLlmModels(ImageUploadOption.imageUrl, response);
    });
  }, [openImageDialog]);

  const filterLlmModels = (selectedOption: ImageUploadOption, llmModels: LlmModel[] = allLlmModels) => {
    const modelIds =
      selectedOption === ImageUploadOption.imageUrl
        ? [llmModelEnum.GPT4o]
        : [llmModelEnum.GPT4o, llmModelEnum.GeminiFlash1_5, llmModelEnum.GeminiFlash2_5, llmModelEnum.GeminiPro1_5];

    const filteredModels = llmModels.filter((model) => modelIds.includes(model.id));

    setLlmModels(filteredModels);

    const defaultModel = filteredModels.find((model) => model.id === llmModelEnum.GPT4o) || LlmModel.defaultModal;
    setSelectedModel(defaultModel);
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as ImageUploadOption.imageUrl | ImageUploadOption.browseImage;
    setImageUploadOption(value);
    filterLlmModels(value, allLlmModels);
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrl(event.target.value);
    setOkButtonEnabled(event.target.value.trim() !== '');
  };

  const convertImageToDataUrl = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const BYTES_IN_KILOBYTE = 1024;
      const MAX_FILE_SIZE_MB = 1;
      if (files && files!.length === 0) return;

      if (findEmptyFileName(files!)) {
        enqueueSnackbar(t('fileUpload.fileIsEmpty', { fileName: findEmptyFileName(files!) }), {
          variant: 'error',
        });
        return;
      }

      const isGPT4o = selectedModel.id === llmModelEnum.GPT4o;
      const allowedTypes = isGPT4o ? supportedImageTypesForGPTModels : supportedImageTypesForGeminiModels;
      const invalidFiles = Array.from(files).filter((file) => !allowedTypes.includes(file.type));

      if (invalidFiles.length > 0) {
        enqueueSnackbar(
          t('imageUpload.invalidFileType', { fileName: invalidFiles.map((file) => file.name).join(', ') }),
          {
            variant: 'error',
          }
        );
        return;
      }

      const TOTAL_FILES_SIZE_IN_BYTES = fileArray.reduce((total, file) => total + file.size, 0);
      const TOTAL_FILES_SIZE_IN_MB = TOTAL_FILES_SIZE_IN_BYTES / (BYTES_IN_KILOBYTE * BYTES_IN_KILOBYTE);

      if (TOTAL_FILES_SIZE_IN_MB > MAX_FILE_SIZE_MB) {
        enqueueSnackbar(t('imageUpload.imageFileLimitExceed', { fileName: findEmptyFileName(files!) }), {
          variant: 'error',
        });
        return;
      }
      try {
        const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
        if (imageFiles.length > 0) {
          const imageUrls = await Promise.all(imageFiles.map((file) => convertImageToDataUrl(file)));
          const newImageFileNames = imageFiles.map((file) => file.name);
          setOkButtonEnabled(true);
          setModifiedUrls(imageUrls);
          setUploadedImageNames(newImageFileNames);
          dispatch(
            updateFileSelectionState({
              responseMessage: false,
            })
          );
        }
      } catch (error) {
        console.error(error);
      }
      event.target.value = '';
    }
  };

  const handleClickOkButton = async () => {
    try {
      if (uploadedImageNames.length > 0) {
        dispatch(
          setImageUploadData({
            imageURLList: modifiedUrls,
            imagesNames: uploadedImageNames,
          })
        );
      } else {
        let urlList = imageUrl.split(',');
        urlList = urlList
          .map((url) => url.trim())
          .filter((url, index, self) => url !== '' && self.indexOf(url) === index);
        const validatedUrls: string[] = [];
        for (const url of urlList) {
          const validateImageURL = await Api.validateInputFileURL(
            url.trim(),
            selectedModel.id as SkillParameters['model_name']
          );
          if (validateImageURL) {
            validatedUrls.push(url.trim());
          }
          continue;
        }

        if (validatedUrls) {
          const imageUrlPattern = /\.(jpg|png|gif|jpeg|webp)$/i;
          // Filter the URL list to include only valid image URLs
          const inValidImageUrls = urlList.filter((imageUrl) => !imageUrlPattern.test(imageUrl.trim()));
          const invalidImageUrlPrefix = urlList.filter((imageUrl) => !imageUrl.startsWith('https'));
          if (inValidImageUrls.length > 0 || invalidImageUrlPrefix.length > 0) {
            enqueueSnackbar(t('imageUpload.uploadValidURLsText'), {
              variant: 'error',
            });
            return;
          }
          if (urlList.length >= 1 && urlList[0]) {
            dispatch(
              setImageUploadData({
                imageURLList: urlList,
                imagesNames: [],
              })
            );
          }
          setImageUploadOption(ImageUploadOption.imageUrl);
        }
      }
      closeImageDialog();
      dispatch(
        setInitialHistoryState({
          selectedModelHistory: selectedModel.id,
        })
      );
    } catch (error) {
      setImageUrl('');
      setOkButtonEnabled(false);
      showSnackbarErrorMessage(error as ApiError);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleModelChange = (event: SelectChangeEvent) => {
    const model = llmModels.find((model) => model.id === event.target.value);
    setSelectedModel(model || LlmModel.defaultModal);
  };

  return (
    <>
      <Box
        component="div"
        sx={{
          [theme.breakpoints.down('sm')]: {
            position: 'static',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            left: '1px',
          }}
        >
          <ImageButton
            disabled={disabled}
            srcFile={assets.imageUploadIcon}
            handleClick={handleImageUploadOptionsClick}
            ariaLabel="Upload Image"
          />
        </Box>
      </Box>
      <Box sx={{ width: '27rem', backgroundColor: 'chocolate' }}>
        {showImageUploadOptions && (
          <Box>
            <Dialog
              open={openImageDialog}
              onClose={closeImageDialog}
              maxWidth="lg"
              PaperProps={{ sx: { width: '35rem' } }}
            >
              <DialogTitle>{t('imageUpload.imageUploadOptionsText')}</DialogTitle>
              <DialogContent>
                <RadioGroup value={imageUploadOption} onChange={handleOptionChange}>
                  <FormControlLabel value="url" control={<Radio />} label="URL" />
                  {imageUploadOption === 'url' && (
                    <TextField
                      id="outlined-multiline-flexible"
                      multiline
                      minRows={2}
                      maxRows={6}
                      label="Enter Image URL"
                      value={imageUrl}
                      onChange={handleUrlChange}
                      sx={{ margin: 'auto', width: '88%' }}
                      fullWidth
                    />
                  )}
                  <FormControlLabel value="browse" control={<Radio />} label="Browse" />
                </RadioGroup>
                {imageUploadOption === 'browse' && (
                  <Box>
                    <Box sx={{ display: 'flex', marginTop: '14px', paddingLeft: '30px' }}>
                      <Button variant="contained" onClick={handleBrowseClick}>
                        {t('imageUpload.browseFilesButtonText')}
                      </Button>
                    </Box>
                    <Box display={'flex'} marginTop={'17px'} paddingLeft={'30px'}>
                      <Typography fontSize={15} color={'#636363'}>
                        {`${t('fileUpload.numberofFilesSelected')} ${uploadedImageNames.length}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Input
                  type="file"
                  inputProps={{
                    accept:
                      selectedModel.id === llmModelEnum.GPT4o
                        ? supportedImageTypesForGPTModels
                        : supportedImageTypesForGeminiModels,
                    multiple: true,
                  }}
                  inputRef={fileInputRef}
                  sx={{
                    position: 'absolute',
                    width: 0,
                    height: 0,
                    overflow: 'hidden',
                    opacity: 0,
                  }}
                  onChange={handleFileSelection}
                />
              </DialogContent>

              <DialogActions sx={{ paddingLeft: '25px', paddingBottom: '14px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingLeft: '30px' }}>
                  <ModelSelect
                    handleModelChange={handleModelChange}
                    selectedModel={selectedModel}
                    llmModels={llmModels}
                  />
                </Box>

                <Button onClick={closeImageDialog}>{t('imageUpload.cancel')}</Button>
                <Button onClick={handleClickOkButton} disabled={!okButtonEnabled}>
                  {t('imageUpload.ok')}
                </Button>
              </DialogActions>

              <Typography
                sx={{
                  display: 'flex',
                  fontSize: '13px',
                  color: '#808080',
                  padding: '0px 54px',
                  marginBottom: '14px',
                }}
              >
                {imageUploadOption === 'url' ? imageUploadNoteUrl : imageUploadNoteBrowser}
              </Typography>
            </Dialog>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ImageUpload;
